import React, { useState, useEffect } from "react";
import { Link } from "../components/L";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit, ExternalLink, Calendar, Users, MessageSquare, HelpCircle, ArrowLeft, Save, X, LayoutDashboard, LogOut, Image, FileText, ClipboardList, Settings, UserPlus, CreditCard, Ticket } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import AdminSettingsPage from "./AdminSettings";
import AdminLeadsPage from "./AdminLeads";
import AdminEventForm from "./AdminEventForm";
import AdminTransactionsPage from "./AdminTransactions";
import AdminVouchersPage from "./AdminVouchers";
import DashboardAnalytics from "./AdminAnalytics";
import { formatPrice } from "../utils/price";
import BadgePhotoModal from "../components/BadgePhotoModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const getAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const links = [
    { path: "/paneli", label: "Dashboard", icon: LayoutDashboard },
    { path: "/paneli/events", label: "Eventet", icon: Calendar },
    { path: "/paneli/trainers", label: "Trajneret", icon: Users },
    { path: "/paneli/gallery", label: "Galeria", icon: Image },
    { path: "/paneli/blog", label: "Blog", icon: FileText },
    { path: "/paneli/contacts", label: "Mesazhet", icon: MessageSquare },
    { path: "/paneli/registrations", label: "Regjistrimet", icon: ClipboardList },
    { path: "/paneli/leads", label: "Leads", icon: UserPlus },
    { path: "/paneli/transactions", label: "Transaksione", icon: CreditCard },
    { path: "/paneli/vouchers", label: "Promo Code", icon: Ticket },
    { path: "/paneli/faq", label: "FAQ", icon: HelpCircle },
    { path: "/paneli/settings", label: "Konfigurime", icon: Settings },
  ];

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* ignore */ }
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  return (
    <aside className="w-64 min-h-screen text-white flex-shrink-0 hidden md:block" style={{ background: 'linear-gradient(180deg, #003F72 0%, #043F46 100%)' }} data-testid="admin-sidebar">
      <div className="p-6 border-b border-white/10">
        <img src="/api/files/local/logo-white.png" alt="Logo" className="h-12 w-auto mb-3" />
        <p className="text-xs text-[#3CD4E1] font-semibold">Admin Panel</p>
      </div>
      <nav className="p-4 space-y-1">
        {links.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            data-testid={`admin-nav-${link.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
              location.pathname === link.path
                ? "bg-[#1CA3B0] text-white"
                : "text-slate-300 hover:bg-white/10 hover:text-white"
            }`}
          >
            <link.icon size={18} />
            {link.label}
          </Link>
        ))}
      </nav>
      <div className="p-4 mt-auto space-y-1">
        <Link
          to="/"
          className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
          data-testid="admin-back-site"
        >
          <ArrowLeft size={18} /> Kthehu ne faqe
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors"
          data-testid="admin-logout-btn"
        >
          <LogOut size={18} /> Dil
        </button>
      </div>
    </aside>
  );
};

const AdminDashboard = () => {
  const [counts, setCounts] = useState({ events: 0, trainers: 0, contacts: 0, faq: 0 });
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [events, trainers, contacts, faq, gallery, blog, registrations, leads, transactions, analyticsRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/trainers`),
          axios.get(`${API}/contacts`),
          axios.get(`${API}/faq`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/blog`),
          axios.get(`${API}/registrations`),
          axios.get(`${API}/leads`),
          axios.get(`${API}/transactions`),
          axios.get(`${API}/analytics/summary`),
        ]);
        setCounts({
          events: events.data.length,
          trainers: trainers.data.length,
          contacts: contacts.data.length,
          faq: faq.data.length,
          gallery: gallery.data.length,
          blog: blog.data.length,
          registrations: registrations.data.length,
          leads: leads.data.length,
          transactions: transactions.data.length,
          analytics: analyticsRes.data,
        });
      } catch (err) {
        console.error(err);
      }
    };
    fetchCounts();
  }, []);

  const cards = [
    { label: "Evente", count: counts.events, icon: Calendar, color: "#08707A", path: "/paneli/events" },
    { label: "Trajnere", count: counts.trainers, icon: Users, color: "#1CA3B0", path: "/paneli/trainers" },
    { label: "Galeria", count: counts.gallery, icon: Image, color: "#065A63", path: "/paneli/gallery" },
    { label: "Blog", count: counts.blog, icon: FileText, color: "#003F72", path: "/paneli/blog" },
    { label: "Mesazhe", count: counts.contacts, icon: MessageSquare, color: "#043F46", path: "/paneli/contacts" },
    { label: "Regjistrimet", count: counts.registrations, icon: ClipboardList, color: "#1d6fa8", path: "/paneli/registrations" },
    { label: "Leads", count: counts.leads, icon: UserPlus, color: "#08707A", path: "/paneli/leads" },
    { label: "Transaksione", count: counts.transactions, icon: CreditCard, color: "#6B21A8", path: "/paneli/transactions" },
    { label: "FAQ", count: counts.faq, icon: HelpCircle, color: "#1d88a8", path: "/paneli/faq" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold font-['Inter_Tight'] mb-8">Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card) => (
          <Link
            key={card.label}
            to={card.path}
            className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
            data-testid={`dashboard-card-${card.label.toLowerCase()}`}
          >
            <card.icon size={24} style={{ color: card.color }} className="mb-3" />
            <p className="text-3xl font-black font-['Inter_Tight']" style={{ color: card.color }}>{card.count}</p>
            <p className="text-sm text-[#828D9E] mt-1">{card.label}</p>
          </Link>
        ))}
      </div>
      {counts.analytics && <DashboardAnalytics data={counts.analytics} />}
    </div>
  );
};

// File upload helper
const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data.url;
};

// Generic CRUD component
const CrudSection = ({ title, endpoint, fields, renderItem }) => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/${endpoint}`);
      setItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => {
    const empty = {};
    fields.forEach((f) => { empty[f.name] = f.default || ""; });
    setForm(empty);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0;
    });
    try {
      if (editingId) {
        await axios.put(`${API}/${endpoint}/${editingId}`, payload);
      } else {
        await axios.post(`${API}/${endpoint}`, payload);
      }
      fetchItems();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error("Ruajtja deshtoi. Kontrolloni fushat dhe provoni perseri.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Jeni te sigurt qe doni ta fshini?")) return;
    try {
      await axios.delete(`${API}/${endpoint}/${id}`);
      fetchItems();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (item) => {
    const formData = {};
    fields.forEach((f) => { formData[f.name] = item[f.name] ?? ""; });
    setForm(formData);
    setEditingId(item.id);
    setShowForm(true);
  };

  return (
    <div data-testid={`admin-${endpoint}`}>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold font-['Inter_Tight']">{title}</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-full px-5 py-2.5 font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center gap-2 bg-[#08707A] text-white shadow-sm hover:bg-[#065A63]"
          data-testid={`add-${endpoint}-btn`}
        >
          <Plus size={16} /> Shto
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => resetForm()}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`${endpoint}-form-modal`}>
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold font-['Inter_Tight']">
                {editingId ? "Edito" : "Shto"} {title.slice(0, -1)}
              </h3>
              <button onClick={resetForm} className="text-slate-400 hover:text-slate-600" data-testid="close-form-btn">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {fields.map((field) => (
                <div key={field.name}>
                  <Label className="text-sm font-semibold text-[#0A0D10] mb-2 block">{field.label}</Label>
                  {field.type === "file" ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#08707A]/10 file:text-[#08707A] hover:file:bg-[#08707A]/20"
                        onChange={async (e) => {
                          const f = e.target.files[0]; if (!f) return;
                          setUploading(true);
                          try { const url = await uploadFile(f); setForm({ ...form, [field.name]: url }); } catch (err) { console.error(err); }
                          setUploading(false);
                        }}
                        data-testid={`form-${field.name}`}
                      />
                      {form[field.name] && <img src={form[field.name].startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form[field.name]}` : form[field.name]} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
                    </div>
                  ) : field.type === "files" ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#08707A]/10 file:text-[#08707A] hover:file:bg-[#08707A]/20"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files); if (!files.length) return;
                          setUploading(true);
                          try {
                            const urls = [];
                            for (const f of files) { urls.push(await uploadFile(f)); }
                            const existing = form[field.name] ? (Array.isArray(form[field.name]) ? form[field.name] : form[field.name].split("\n").filter(Boolean)) : [];
                            setForm({ ...form, [field.name]: [...existing, ...urls] });
                          } catch (err) { console.error(err); }
                          setUploading(false);
                        }}
                        data-testid={`form-${field.name}`}
                      />
                      {uploading && <p className="text-xs text-[#1CA3B0] mt-1">Duke ngarkuar...</p>}
                      {form[field.name] && Array.isArray(form[field.name]) && form[field.name].length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {form[field.name].map((url, idx) => (
                            <div key={idx} className="relative">
                              <img src={url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt="" className="h-16 w-16 rounded-lg object-cover" />
                              <button type="button" onClick={() => setForm({ ...form, [field.name]: form[field.name].filter((_, j) => j !== idx) })} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">x</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : field.type === "textarea" ? (
                    <Textarea
                      value={form[field.name] || ""}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                      rows={3}
                      className="rounded-xl border-slate-200 bg-slate-50"
                      data-testid={`form-${field.name}`}
                    />
                  ) : field.type === "select" ? (
                    <select
                      value={form[field.name] || ""}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm"
                      data-testid={`form-${field.name}`}
                    >
                      {field.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      type={field.type || "text"}
                      value={form[field.name] || ""}
                      onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
                      className="rounded-xl border-slate-200 bg-slate-50"
                      data-testid={`form-${field.name}`}
                    />
                  )}
                </div>
              ))}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={uploading}
                  className="rounded-full px-6 py-2.5 font-semibold text-sm inline-flex items-center gap-2 text-white transition-colors disabled:opacity-50"
                  style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }}
                  data-testid="save-form-btn"
                >
                  <Save size={16} /> Ruaj
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-full px-6 py-2.5 font-semibold text-sm border border-slate-200 text-[#4A5568] hover:bg-slate-50 transition-colors"
                  data-testid="cancel-form-btn"
                >
                  Anulo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="space-y-3">
        {items.length === 0 ? (
          <p className="text-[#828D9E] text-center py-12">Nuk ka te dhena.</p>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`item-${item.id}`}
            >
              <div className="flex-1 min-w-0">
                {renderItem(item)}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {endpoint === "trainers" && (
                  <a
                    href={`/trajnere/${item.slug || item.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#1CA3B0] hover:bg-[#1CA3B0]/10 rounded-lg transition-colors"
                    data-testid={`open-${item.id}`}
                    title="Hap ne nje tab te ri"
                    aria-label="Hap"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
                {endpoint === "blog" && (
                  <a
                    href={`/blog/${item.slug || item.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#1CA3B0] hover:bg-[#1CA3B0]/10 rounded-lg transition-colors"
                    data-testid={`open-${item.id}`}
                    title="Hap ne nje tab te ri"
                    aria-label="Hap"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
                {endpoint !== "contacts" && (
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 text-[#08707A] hover:bg-[#08707A]/10 rounded-lg transition-colors"
                    data-testid={`edit-${item.id}`}
                  >
                    <Edit size={16} />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`delete-${item.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/events`)
      .then((r) => {
        // Sort newest -> oldest by parsing DD/MM/YYYY (range like 10-11/12/2026 uses last day)
        const parseDate = (d) => {
          if (!d) return 0;
          const parts = String(d).split("/");
          if (parts.length !== 3) return 0;
          const dayPart = parts[0].split("-").pop();
          const y = parseInt(parts[2], 10);
          const m = parseInt(parts[1], 10);
          const day = parseInt(dayPart, 10);
          if (!y || !m || !day) return 0;
          return new Date(y, m - 1, day).getTime();
        };
        const sorted = [...r.data].sort((a, b) => parseDate(b.date) - parseDate(a.date));
        setEvents(sorted);
      })
      .catch(console.error);
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Jeni te sigurt qe doni ta fshini kete event?")) return;
    try {
      await axios.delete(`${API}/events/${id}`);
      setEvents(events.filter((e) => e.id !== id));
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="admin-events">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold font-['Inter_Tight']">Eventet</h1>
        <button
          onClick={() => navigate("/paneli/events/new")}
          className="rounded-full px-5 py-2.5 font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center gap-2 bg-[#08707A] text-white shadow-sm hover:bg-[#065A63]"
          data-testid="add-events-btn"
        >
          <Plus size={16} /> Shto
        </button>
      </div>
      <div className="space-y-3">
        {events.length === 0 ? (
          <p className="text-[#828D9E] text-center py-12">Nuk ka evente.</p>
        ) : events.map((item) => (
          <div key={item.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`item-${item.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-[#0A0D10] truncate">{item.title}</p>
              <p className="text-xs text-[#828D9E] mt-1">{item.date} | {item.category === "upcoming" ? "E ardhshme" : "E kaluar"} | {item.price ? formatPrice(item.price) : "Falas"}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href={`/evente/${item.slug || item.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-[#1CA3B0] hover:bg-[#1CA3B0]/10 rounded-lg transition-colors"
                data-testid={`open-${item.id}`}
                title="Hap eventin ne nje tab te ri"
                aria-label="Hap eventin"
              >
                <ExternalLink size={16} />
              </a>
              <button onClick={() => navigate(`/paneli/events/edit/${item.id}`)} className="p-2 text-[#08707A] hover:bg-[#08707A]/10 rounded-lg transition-colors" data-testid={`edit-${item.id}`}>
                <Edit size={16} />
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-${item.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminTrainers = () => (
  <CrudSection
    title="Trajneret"
    endpoint="trainers"
    fields={[
      { name: "name", label: "Emri", type: "text" },
      { name: "specialty", label: "Specialiteti", type: "text" },
      { name: "bio", label: "Bio", type: "textarea" },
      { name: "experience", label: "Pervoja", type: "textarea" },
      { name: "trainings", label: "Trajnimet (ndare me presje)", type: "textarea" },
      { name: "image_url", label: "Foto e Profilit", type: "file" },
      { name: "order", label: "Renditja", type: "number", default: "0" },
    ]}
    renderItem={(item) => (
      <>
        <p className="font-semibold text-sm text-[#0A0D10]">{item.name}</p>
        <p className="text-xs text-[#828D9E] mt-1">{item.specialty}</p>
      </>
    )}
  />
);

const AdminContacts = () => (
  <CrudSection
    title="Mesazhet"
    endpoint="contacts"
    fields={[]}
    renderItem={(item) => (
      <>
        <p className="font-semibold text-sm text-[#0A0D10]">{item.first_name} {item.last_name}</p>
        <p className="text-xs text-[#828D9E] mt-1">{item.email} | {item.subject || "Pa subjekt"}</p>
        <p className="text-xs text-[#4A5568] mt-1 line-clamp-1">{item.message}</p>
      </>
    )}
  />
);

const AdminFAQ = () => (
  <CrudSection
    title="FAQ"
    endpoint="faq"
    fields={[
      { name: "question", label: "Pyetja", type: "text" },
      { name: "answer", label: "Pergjigja", type: "textarea" },
      { name: "order", label: "Renditja", type: "number", default: "0" },
    ]}
    renderItem={(item) => (
      <>
        <p className="font-semibold text-sm text-[#0A0D10] truncate">{item.question}</p>
        <p className="text-xs text-[#4A5568] mt-1 line-clamp-1">{item.answer}</p>
      </>
    )}
  />
);

const AdminGallery = () => (
  <CrudSection
    title="Galeria"
    endpoint="gallery"
    fields={[
      { name: "title", label: "Titulli i Albumit", type: "text" },
      { name: "event_name", label: "Emri i Eventit", type: "text" },
      { name: "cover_image", label: "Imazhi Kryesor", type: "file" },
      { name: "images", label: "Imazhet e Albumit", type: "files" },
    ]}
    renderItem={(item) => (
      <>
        <p className="font-semibold text-sm text-[#0A0D10]">{item.title}</p>
        <p className="text-xs text-[#828D9E] mt-1">{item.event_name} | {item.images?.length || 0} foto</p>
      </>
    )}
  />
);

const AdminBlog = () => (
  <CrudSection
    title="Blog"
    endpoint="blog"
    fields={[
      { name: "title", label: "Titulli", type: "text" },
      { name: "excerpt", label: "Permbledhje", type: "textarea" },
      { name: "content", label: "Permbajtja", type: "textarea" },
      { name: "cover_image", label: "Foto Kryesore", type: "file" },
      { name: "author", label: "Autori", type: "text", default: "Albanian Aligners Academy" },
      { name: "category", label: "Kategoria", type: "select", default: "Aligners", options: [
        { value: "Aligners", label: "Aligners" },
        { value: "Ortodonci", label: "Ortodonci" },
        { value: "Estetike Dentare", label: "Estetike Dentare" },
        { value: "Planifikim Digjital", label: "Planifikim Digjital" },
        { value: "Edukim Profesional", label: "Edukim Profesional" },
        { value: "Evente dhe Njoftime", label: "Evente dhe Njoftime" },
      ]},
    ]}
    renderItem={(item) => (
      <>
        <p className="font-semibold text-sm text-[#0A0D10] truncate">{item.title}</p>
        <p className="text-xs text-[#828D9E] mt-1">{item.category} | {item.author}</p>
      </>
    )}
  />
);

const AdminRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [events, setEvents] = useState([]);
  const [filterEvent, setFilterEvent] = useState("all");
  const [viewingBadge, setViewingBadge] = useState(null); // {src, name} | null

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [regsRes, eventsRes] = await Promise.all([
          axios.get(`${API}/registrations`),
          axios.get(`${API}/events`),
        ]);
        setRegistrations(regsRes.data);
        setEvents(eventsRes.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Jeni te sigurt qe doni ta fshini kete regjistrim?")) return;
    try {
      await axios.delete(`${API}/registrations/${id}`);
      setRegistrations(registrations.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = filterEvent === "all" ? registrations : registrations.filter(r => r.event_id === filterEvent);

  const groupedByEvent = {};
  filtered.forEach(r => {
    const key = r.event_title || r.event_id;
    if (!groupedByEvent[key]) groupedByEvent[key] = [];
    groupedByEvent[key].push(r);
  });

  return (
    <div data-testid="admin-registrations">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold font-['Inter_Tight']">Regjistrimet per Evente</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#828D9E]">Filtro:</span>
          <select
            value={filterEvent}
            onChange={(e) => setFilterEvent(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
            data-testid="reg-filter-select"
          >
            <option value="all">Te gjitha ({registrations.length})</option>
            {events.map(ev => {
              const count = registrations.filter(r => r.event_id === ev.id).length;
              return <option key={ev.id} value={ev.id}>{ev.title} ({count})</option>;
            })}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <ClipboardList className="mx-auto text-[#828D9E] mb-4" size={48} />
          <p className="text-[#828D9E] text-lg">Nuk ka regjistrime.</p>
        </div>
      ) : (
        Object.entries(groupedByEvent).map(([eventTitle, regs]) => (
          <div key={eventTitle} className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <Calendar size={18} className="text-[#1CA3B0]" />
              <h3 className="font-bold text-base text-[#0A0D10]">{eventTitle}</h3>
              <span className="text-xs font-semibold text-white px-2.5 py-0.5 rounded-full" style={{ background: '#1CA3B0' }}>{regs.length}</span>
            </div>
            <div className="space-y-2">
              {regs.map((reg) => (
                <div
                  key={reg.id}
                  className="bg-white border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-4 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`reg-item-${reg.id}`}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {reg.badge_photo ? (
                      <button
                        type="button"
                        onClick={() => setViewingBadge({ src: reg.badge_photo, name: `${reg.first_name} ${reg.last_name}` })}
                        title="Shiko foton e badge-it"
                        className="shrink-0 rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1CA3B0]"
                        data-testid={`view-badge-${reg.id}`}
                      >
                        <img
                          src={reg.badge_photo.startsWith("http") ? reg.badge_photo : `${BACKEND_URL}${reg.badge_photo}`}
                          alt="Badge"
                          className="w-14 h-14 object-cover border border-slate-200 hover:ring-2 hover:ring-[#1CA3B0] transition-all"
                        />
                      </button>
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0" title="Pa foto badge">
                        <i className="fas fa-user text-lg"></i>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-[#0A0D10]">{reg.first_name} {reg.last_name}</p>
                        {reg.payment_status && reg.payment_status !== "free" && (
                          <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={
                              reg.payment_status === "paid"
                                ? { background: "#DCFCE7", color: "#166534" }
                                : reg.payment_status === "pending"
                                ? { background: "#FEF3C7", color: "#92400E" }
                                : { background: "#F1F5F9", color: "#475569" }
                            }
                          >
                            {reg.payment_status === "paid" ? "Paguar" : reg.payment_status === "pending" ? "Ne pritje" : reg.payment_status}
                          </span>
                        )}
                        {reg.voucher_code && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E0E7FF] text-[#3730A3]" title="Promo Code i perdorur">
                            <i className="fas fa-ticket-alt mr-1"></i>{reg.voucher_code}
                          </span>
                        )}
                        {reg.tier_label && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#1CA3B0]/10 text-[#08707A] uppercase tracking-wider">
                            {reg.tier_label}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#828D9E] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span><i className="fas fa-envelope mr-1"></i>{reg.email}</span>
                        {reg.phone && <span><i className="fas fa-phone mr-1"></i>{reg.phone}</span>}
                        {reg.profession && <span><i className="fas fa-briefcase mr-1"></i>{reg.profession}</span>}
                        {reg.city && <span><i className="fas fa-map-marker-alt mr-1"></i>{reg.city}</span>}
                        <span><i className="fas fa-users mr-1"></i>{reg.num_participants} pjesemarres</span>
                      </p>
                      {reg.message && <p className="text-xs text-[#4A5568] mt-1 line-clamp-2"><i className="fas fa-comment mr-1"></i>{reg.message}</p>}
                      {(reg.accepted_terms || reg.marketing_consent) && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {reg.accepted_terms && <span className="inline-flex items-center gap-1 text-[10px] text-[#166534] bg-[#DCFCE7] px-1.5 py-0.5 rounded"><i className="fas fa-check"></i> Kushtet</span>}
                          {reg.marketing_consent && <span className="inline-flex items-center gap-1 text-[10px] text-[#1E40AF] bg-[#DBEAFE] px-1.5 py-0.5 rounded"><i className="fas fa-envelope-open-text"></i> Marketing</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(reg.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    data-testid={`delete-reg-${reg.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
      <BadgePhotoModal src={viewingBadge?.src} name={viewingBadge?.name} onClose={() => setViewingBadge(null)} />
    </div>
  );
};

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthenticated(true))
      .catch(() => {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      });
  }, [navigate]);

  if (authenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <p className="text-[#828D9E]">Duke verifikuar...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#FAFAFA]" data-testid="admin-page">
      <AdminSidebar />

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 text-white py-3 px-4 z-50 flex items-center justify-between" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }}>
        <span className="font-bold text-sm">Aligners Academy</span>
        <Link to="/" className="text-sm text-white/80">Kthehu</Link>
      </div>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 flex" data-testid="admin-mobile-nav">
        {[
          { path: "/paneli", icon: LayoutDashboard, label: "Home" },
          { path: "/paneli/events", icon: Calendar, label: "Evente" },
          { path: "/paneli/trainers", icon: Users, label: "Trajnere" },
          { path: "/paneli/contacts", icon: MessageSquare, label: "Mesazhe" },
          { path: "/paneli/registrations", icon: ClipboardList, label: "Regjist." },
          { path: "/paneli/leads", icon: UserPlus, label: "Leads" },
          { path: "/paneli/transactions", icon: CreditCard, label: "Pagesa" },
          { path: "/paneli/faq", icon: HelpCircle, label: "FAQ" },
          { path: "/paneli/settings", icon: Settings, label: "Konfig." },
        ].map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="flex-1 flex flex-col items-center py-3 text-xs text-[#828D9E] hover:text-[#08707A]"
          >
            <item.icon size={18} />
            <span className="mt-1">{item.label}</span>
          </Link>
        ))}
      </div>

      <main className="flex-1 p-6 md:p-10 mt-12 md:mt-0 mb-16 md:mb-0">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="events/new" element={<AdminEventForm />} />
          <Route path="events/edit/:eventId" element={<AdminEventForm />} />
          <Route path="trainers" element={<AdminTrainers />} />
          <Route path="gallery" element={<AdminGallery />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="registrations" element={<AdminRegistrations />} />
          <Route path="leads" element={<AdminLeadsPage />} />
          <Route path="transactions" element={<AdminTransactionsPage />} />
          <Route path="vouchers" element={<AdminVouchersPage />} />
          <Route path="faq" element={<AdminFAQ />} />
          <Route path="settings" element={<AdminSettingsPage />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminPage;
